import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card, Grid, Divider, Chip, Icon, Alert, Snackbar,
    TextField, Select, MenuItem, FormControl, Autocomplete, CircularProgress,
} from "@mui/material";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MonoText from "components/MonoText";
import PearlLogoLoader from "components/PearlLogoLoader";
import { useAuth } from "auth/Auth";
import { differenceInYears } from "date-fns";
import { createFilterOptions } from "@mui/material/Autocomplete";
import AddressAutocompleteInput from "layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput";
import { GENDER_OPTIONS, SPECIAL_DETAILS_MAX_LENGTH } from "layouts/Travel/orders/hardcodedData";

// Date picker
// DesktopDatePicker keeps the text field editable on touch devices, where the
// responsive DatePicker would fall back to a read-only, calendar-only input.
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Custom Hooks
import { useCountries } from "hooks/useCountries";
import { useCountryCodes } from "hooks/useCountryCodes";
import EditIcon from "@mui/icons-material/Edit";
import { getApiErrorMessage } from "utils/apiErrors"

// -------------------------------------------------
// Field mapping: wizard field -> pax record field
// Only fields on this list are editable inline. Everything else on the
// detail page stays permanently read-only, even while in edit mode.
// -------------------------------------------------
// wizard: first_name        -> pax: first_name
// wizard: middle_name       -> pax: middle_name
// wizard: last_name         -> pax: last_name
// wizard: dateOfBirth       -> pax: date_of_birth
// wizard: gender            -> pax: gender
// wizard: nationality       -> pax: nationality
// wizard: email             -> pax: email
// wizard: phoneCountryCode  -> pax: dial_code
// wizard: phone             -> pax: mobile
// wizard: passportNumber    -> pax: passport_number
// wizard: passportIssueDate -> pax: passport_issue_date
// wizard: passportExpiry    -> pax: passport_expiry_date
// wizard: passportLocation  -> pax: passport_issue_place
// wizard: address           -> pax: address
// wizard: country           -> pax: country
// wizard: specialDetails    -> pax: special_request

const DATE_INPUT_FORMAT = "dd/MM/yyyy";

const filterCountryCodeOptions = createFilterOptions({
    stringify: (option) => `${option.dialCode} ${option.country}`,
});

// Same required set as the wizard. Passport fields are optional.
const REQUIRED_FIELDS = [
    ["first_name", "First name"],
    ["date_of_birth", "Date of birth"],
    ["gender", "Gender"],
    ["nationality", "Nationality"],
    ["email", "Email"],
    ["dial_code", "Phone country code"],
    ["mobile", "Phone"],
    ["address", "Address"],
    ["country", "Country"],
];

// -------------------------------------------------
// Helpers
// -------------------------------------------------

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = parseLocalDate(dateStr);
    if (!d) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};


const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const timePart = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${datePart}, ${timePart}`;
};

const getAge = (dobStr) => {
    if (!dobStr) return null;
    const dob = parseLocalDate(dobStr);
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const hasHadBirthdayThisYear =
        today.getMonth() > dob.getMonth() ||
        (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
    if (!hasHadBirthdayThisYear) age -= 1;
    return age;
};

const getInitials = (name) => {
    if (!name) return "-";
    const parts = name.trim().split(/\s+/);
    return parts.length > 1
        ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const getPassportExpiryStatus = (expiryDateStr) => {
    if (!expiryDateStr) return null;
    const expiry = parseLocalDate(expiryDateStr);
    if (!expiry) return null;
    const today = new Date();
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(today.getMonth() + 6);

    if (expiry < today) return { label: "Expired", color: "#C62828", bg: "#FDECEA" };
    if (expiry < sixMonthsFromNow) return { label: "Expiring Soon", color: "#E68A06", bg: "#FFF3E0" };
    return { label: "Valid", color: "#2E7D32", bg: "#E8F5E9" };
};

const toComparableDate = (value) => {
    if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) {
        return new Date(`1970-01-01T${value}:00`);
    }
    return parseLocalDate(value) || new Date(value);
};

// Safely parses "YYYY-MM-DD" (or Date object) into a local Date without UTC offset shift
const parseLocalDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === "string") {
        const datePart = value.split("T")[0].trim();
        const parts = datePart.split("-");
        if (parts.length === 3) {
            const [year, month, day] = parts.map(Number);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                return new Date(year, month - 1, day);
            }
        }
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

// Formats a Date or string to "YYYY-MM-DD" for API payloads
const toApiDateString = (value) => {
    if (!value) return null;
    if (typeof value === "string") {
        const trimmed = value.trim();
        // If it's already "YYYY-MM-DD", send as-is
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
            return trimmed;
        }
    }
    const d = parseLocalDate(value);
    if (!d) return null;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};


const PASSENGER_NAME_MAX = 40; // same cap as the wizard

// Full name built from first/middle/last. Used for the live header preview
// and for the passenger_name sent to the server, so they always match.
const composeDisplayName = (data) => {
    const parts = [data.first_name, data.middle_name, data.last_name]
        .map((p) => (typeof p === "string" ? p.trim() : p))
        .filter(Boolean);
    return parts.length
        ? parts.join(" ").slice(0, PASSENGER_NAME_MAX).trim()
        : data.passenger_name;
};

// -------------------------------------------------
// Validation — ported from TravelerDetailsStep.validate(),
// scoped to a single traveler record using pax field names.
// Passport validity is verified against departure_date + 6 months.
// -------------------------------------------------


const validateEditedPax = (data, { isValidPhoneNumber, departureDate }) => {
    const errors = {};
    let firstMessage = "";
    let firstErrorField = null;

    // Keep the first error per field, so "Phone is required." isn't
    // overwritten by "invalid phone".
    const setError = (field, message) => {
        if (errors[field]) return;
        errors[field] = message;
        if (!firstMessage) {
            firstMessage = message;
            firstErrorField = field;
        }
    };

    REQUIRED_FIELDS.forEach(([field, label]) => {
        const value = data[field];
        const isEmpty = !value || (typeof value === "string" && !value.trim());
        if (isEmpty) setError(field, `${label} is required.`);
    });

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (data.email && !emailPattern.test(data.email.trim())) {
        setError("email", "Please enter a valid email address.");
    }

    if (!isValidPhoneNumber({ dialCode: data.dial_code }, data.mobile)) {
        setError("mobile", "Valid phone number with country code is required.");
    }

    if (data.emergency_email && !emailPattern.test(data.emergency_email.trim())) {
        setError("emergency_email", "Please enter a valid email address.");
    }

    // ---- Date of birth ----
    if (data.date_of_birth) {
        const dob = parseLocalDate(data.date_of_birth);
        if (!dob) {
            setError("date_of_birth", "Please enter a valid date.");
        } else {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            dob.setHours(0, 0, 0, 0);

            if (dob > today) {
                setError("date_of_birth", "Date of birth can't be in the future.");
            } else if (data.is_primary && differenceInYears(new Date(), dob) < 18) {
                setError("date_of_birth", "Primary traveler must be at least 18 years old.");
            }
        }
    }

    // ---- Passport (all optional, validated only if filled) ----
    const issueValid = data.passport_issue_date && Boolean(parseLocalDate(data.passport_issue_date));
    const expiryValid = data.passport_expiry_date && Boolean(parseLocalDate(data.passport_expiry_date));

    if (data.passport_issue_date && !issueValid) {
        setError("passport_issue_date", "Please enter a valid date.");
    }
    if (data.passport_expiry_date && !expiryValid) {
        setError("passport_expiry_date", "Please enter a valid date.");
    }

    if (issueValid && expiryValid) {
        if (toComparableDate(data.passport_expiry_date) <= toComparableDate(data.passport_issue_date)) {
            setError("passport_expiry_date", "Passport expiry date must come after passport issuing date.");
        }
    }

    // Passport must be valid 6 months past departure (matches server validation)
    if (departureDate && expiryValid) {
        const dep = parseLocalDate(departureDate) || new Date(departureDate);
        if (!isNaN(dep.getTime())) {
            const minExpiry = new Date(dep);
            minExpiry.setMonth(minExpiry.getMonth() + 6);
            if (parseLocalDate(data.passport_expiry_date) < minExpiry) {
                setError(
                    "passport_expiry_date",
                    `Passport must be valid until at least ${minExpiry.toLocaleDateString("en-IN", {
                        day: "2-digit", month: "short", year: "numeric",
                    })} (6 months after departure).`
                );
            }
        }
    }

    return { isValid: Object.keys(errors).length === 0, errors, firstMessage, firstErrorField };
};

// -------------------------------------------------
// Shared small components
// -------------------------------------------------

function InfoCard({ icon, title, headerAction, children }) {
    return (
        <Card sx={{ borderRadius: "12px", border: "1px solid #E8EDF5", boxShadow: "none", height: "100%", overflow: "hidden" }}>
            <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1.25}
                sx={{ bgcolor: "#F4F6F9", borderBottom: "1px solid #E8EDF5" }}
            >
                <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 20, color: "#5C6B82" }}>{icon}</Icon>
                    <MDTypography variant="button" fontWeight="bold" color="dark">
                        {title}
                    </MDTypography>
                </MDBox>
                {headerAction}
            </MDBox>
            <MDBox p={2.5}>{children}</MDBox>
        </Card>
    );
}

InfoCard.propTypes = {
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    headerAction: PropTypes.node,
    children: PropTypes.node,
};

// Always read-only. Used both for genuinely non-editable fields and as
// the "not currently in edit mode" rendering for editable ones.
function DetailField({ label, value, xs = 12, md = 4 }) {
    return (
        <Grid item xs={xs} md={md}>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                {label}
            </MDTypography>
            <MDTypography variant="button" fontWeight="regular" color="dark">
                {value || "-"}
            </MDTypography>
        </Grid>
    );
}

DetailField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node,
    xs: PropTypes.number,
    md: PropTypes.number,
};

// Renders DetailField's read-only look when not editing, or the matching
// input control when editing — same Grid item, same xs/md, so the layout
// never shifts when toggling modes.
function EditableField({
    label, fieldKey, value, xs = 12, md = 4, isEditing, type = "text",
    onChange, options = [], multiline = false, displayValue, error,
    required = false, placeholder, maxLength,
}) {
    if (!isEditing) {
        return <DetailField label={label} value={displayValue !== undefined ? displayValue : value} xs={xs} md={md} />;
    }

    return (
        <Grid item xs={xs} md={md} id={`edit-field-${fieldKey}`}>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                {label}
                {required && <MDBox component="span" sx={{ color: "#E53935", ml: 0.25 }}>*</MDBox>}
            </MDTypography>

            {type === "select" && (
                <FormControl fullWidth size="small" error={Boolean(error)}>
                    <Select value={value || ""} displayEmpty onChange={(e) => onChange(e.target.value)}>
                        <MenuItem value="" disabled>Select</MenuItem>
                        {options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {type === "autocomplete" && (
                <Autocomplete
                    size="small"
                    options={options}
                    value={value || null}
                    onChange={(_, newVal) => onChange(newVal || "")}
                    renderInput={(params) => (
                        <TextField {...params} placeholder={`Select ${label.toLowerCase()}`} error={Boolean(error)} />
                    )}
                />
            )}

            {type === "date" && (
                <DesktopDatePicker
                    value={parseLocalDate(value)}
                    onChange={(newVal) => onChange(newVal)}
                    format={DATE_INPUT_FORMAT}
                    slotProps={{ textField: { size: "small", fullWidth: true, error: Boolean(error) } }}
                />
            )}

            {type === "address" && (
                <AddressAutocompleteInput
                    placeholder={placeholder || label}
                    value={value || ""}
                    onChange={onChange}
                    error={Boolean(error)}
                    required={required}
                    compact
                />
            )}

            {type === "text" && (
                <TextField
                    fullWidth
                    size="small"
                    multiline={multiline}
                    minRows={multiline ? 3 : undefined}
                    placeholder={placeholder}
                    value={value || ""}
                    error={Boolean(error)}
                    onChange={(e) =>
                        onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)
                    }
                    helperText={maxLength ? `${(value || "").length}/${maxLength} characters` : undefined}
                />
            )}

            {error && (
                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                    {error}
                </MDTypography>
            )}
        </Grid>
    );
}

EditableField.propTypes = {
    label: PropTypes.string.isRequired,
    fieldKey: PropTypes.string,
    value: PropTypes.any,
    xs: PropTypes.number,
    md: PropTypes.number,
    isEditing: PropTypes.bool,
    type: PropTypes.oneOf(["text", "select", "autocomplete", "date", "address"]),
    onChange: PropTypes.func,
    options: PropTypes.array,
    multiline: PropTypes.bool,
    displayValue: PropTypes.node,
    error: PropTypes.string,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number
};

function EmptyFieldsNotice({ message }) {
    return (
        <MDTypography variant="caption" color="text" sx={{ fontStyle: "italic", opacity: 0.7 }}>
            {message}
        </MDTypography>
    );
}

EmptyFieldsNotice.propTypes = {
    message: PropTypes.string.isRequired,
};


// -------------------------------------------------
// Cancellation & refund (cancelled travelers only)
// -------------------------------------------------

const REFUND_STATUS_STYLES = {
    NA: { label: "No Refund", color: "#616161", bg: "#F0F0F0" },
    Initiated: { label: "Refund Initiated", color: "#E68A06", bg: "#FFF3E0" },
    Processing: { label: "Refund Processing", color: "#E68A06", bg: "#FFF3E0" },
    Refunded: { label: "Refunded", color: "#2E7D32", bg: "#E8F5E9" },
    "Coupon Generated": { label: "Coupon Generated", color: "#1565C8", bg: "#E3F2FD" },
    Failed: { label: "Refund Failed", color: "#C62828", bg: "#FDECEA" },
};

const COUPON_STATUS_STYLES = {
    Active: { color: "#2E7D32", bg: "#E8F5E9" },
    "Partially Used": { color: "#E68A06", bg: "#FFF3E0" },
    "Fully Used": { color: "#616161", bg: "#F0F0F0" },
    Expired: { color: "#C62828", bg: "#FDECEA" },
    Inactive: { color: "#616161", bg: "#F0F0F0" },
};

// Date-only strings (YYYY-MM-DD) are parsed as local dates so they never shift a day.
const formatDateOnly = (value) => {
    if (!value) return "-";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatMoney = (amount, currency) => {
    if (amount === null || amount === undefined || amount === "") return "-";
    const number = Number(amount);
    if (Number.isNaN(number)) return "-";
    try {
        return new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "GBP" }).format(number);
    } catch (e) {
        return `${currency || ""} ${number.toFixed(2)}`.trim();
    }
};

const describeRule = (term) => {
    if (!term) return "No rule matched - non-refundable";
    const parts = [term.name];
    if (term.days !== null && term.days !== undefined) parts.push(`${term.days} days before departure`);
    if (term.amount_percent !== null && term.amount_percent !== undefined) {
        parts.push(`${term.amount_percent}% refund`);
    } else if (term.amount_actual !== null && term.amount_actual !== undefined) {
        parts.push(`flat ${term.amount_actual} refund`);
    }
    return parts.join(" · ");
};

const noCouponMessage = (cancellation) => {
    if (cancellation?.coupon_id || cancellation?.payment_term?.refund_method === "Coupon") {
        return "A coupon was generated for this cancellation but it could not be found.";
    }
    switch (cancellation?.refund_status) {
        case "Initiated":
        case "Processing":
        case "Refunded":
        case "Failed":
            return "This cancellation is refunded directly, so no coupon was issued.";
        case "Coupon Generated":
            return "A coupon was generated for this cancellation but it could not be found.";
        default:
            return "No refund was due for this cancellation, so no coupon was issued.";
    }
};

function RefundStatusChip({ status }) {
    const style = REFUND_STATUS_STYLES[status] || REFUND_STATUS_STYLES.NA;
    return (
        <Chip
            size="small"
            label={style.label}
            sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: "0.7rem" }}
        />
    );
}

RefundStatusChip.propTypes = {
    status: PropTypes.string,
};

function CancellationSectionTitle({ children }) {
    return (
        <MDTypography variant="overline" fontWeight="bold" color="text" display="block" mb={1.5}>
            {children}
        </MDTypography>
    );
}

CancellationSectionTitle.propTypes = {
    children: PropTypes.node,
};

function PaxCancellationDetails({ cancellation, loading, error }) {
    if (loading) {
        return (
            <MDBox display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <MDTypography variant="caption" color="text">Loading cancellation details...</MDTypography>
            </MDBox>
        );
    }
    if (error) return <Alert severity="warning">{error}</Alert>;
    if (!cancellation) {
        return <EmptyFieldsNotice message="No cancellation record found for this traveler." />;
    }

    const { currency, payment_term: term, coupon } = cancellation;
    const couponStyle = coupon ? COUPON_STATUS_STYLES[coupon.status] || COUPON_STATUS_STYLES.Inactive : null;

    return (
        <MDBox>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <CancellationSectionTitle>Cancellation Record</CancellationSectionTitle>
                    <Grid container rowSpacing={2.5} columnSpacing={2}>
                        <DetailField label="Record No." value={<MonoText>{cancellation.name}</MonoText>} xs={6} md={6} />
                        <DetailField label="Cancellation Type" value={cancellation.cancellation_type} xs={6} md={6} />
                        <DetailField label="Requested On" value={formatDateTime(cancellation.requested_on)} xs={6} md={6} />
                        <DetailField label="Status" value={cancellation.status} xs={6} md={6} />
                        <DetailField label="Comments" value={cancellation.comments} xs={12} md={12} />
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <CancellationSectionTitle>Refund Breakdown</CancellationSectionTitle>
                    <Grid container rowSpacing={2.5} columnSpacing={2}>
                        <DetailField label="Amount Paid (Traveler Share)" value={formatMoney(cancellation.total_amount, currency)} xs={6} md={6} />
                        <DetailField label="Cancellation Deduction" value={formatMoney(cancellation.amount_deduction, currency)} xs={6} md={6} />
                        <DetailField
                            label="Refundable Amount"
                            value={<strong>{formatMoney(cancellation.refund_amount, currency)}</strong>}
                            xs={6} md={6}
                        />
                        <DetailField label="Refund Method" value={term?.refund_method} xs={6} md={6} />
                        <DetailField label="Rule Applied" value={describeRule(term)} xs={12} md={12} />
                    </Grid>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <CancellationSectionTitle>Allocated Coupon</CancellationSectionTitle>
                {coupon && (
                    <Chip
                        size="small"
                        label={coupon.status}
                        sx={{ bgcolor: couponStyle.bg, color: couponStyle.color, fontWeight: 700, fontSize: "0.7rem" }}
                    />
                )}
            </MDBox>

            {coupon ? (
                <>
                    <Grid container rowSpacing={2.5} columnSpacing={2}>
                        <DetailField label="Coupon Name" value={<MonoText>{coupon.name}</MonoText>} xs={6} md={3} />
                        <DetailField label="Coupon Code" value={<MonoText>{coupon.code}</MonoText>} xs={6} md={3} />
                        <DetailField label="Coupon Type" value={coupon.coupon_type} xs={6} md={3} />
                        <DetailField
                            label="Coupon Value"
                            value={<strong>{formatMoney(coupon.value, coupon.currency)}</strong>}
                            xs={6} md={3}
                        />
                        <DetailField label="Activation Date" value={formatDateOnly(coupon.activation_date)} xs={6} md={3} />
                        <DetailField label="Expiration Date" value={formatDateOnly(coupon.expiration_date)} xs={6} md={3} />
                        <DetailField label="Redemptions Used" value={`${coupon.used_count} of ${coupon.available_count}`} xs={6} md={3} />
                        <DetailField label="Issued To (Account)" value={coupon.customer_name || "No account linked"} xs={6} md={3} />
                    </Grid>
                    <MDTypography variant="caption" color="text" display="block" mt={2} sx={{ opacity: 0.7 }}>
                        Cancellation coupons are issued to the primary traveler&apos;s account.
                    </MDTypography>
                </>
            ) : (
                <EmptyFieldsNotice message={noCouponMessage(cancellation)} />
            )}
        </MDBox>
    );
}

PaxCancellationDetails.propTypes = {
    cancellation: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
};


// -------------------------------------------------
// Main component
// -------------------------------------------------

function PaxDetailView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [pax, setPax] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [cancellation, setCancellation] = useState(null);
    const [cancellationLoading, setCancellationLoading] = useState(false);
    const [cancellationError, setCancellationError] = useState("");

    // Order cancelled or failed check
    const isOrderCancelledOrFailed = ["cancelled", "cancellation", "failed"].includes(
        String(pax?.order_status || "").trim().toLowerCase()
    );

    // Edit mode state
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedPax, setEditedPax] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    const { countries } = useCountries();
    const { countryCodes, getByDialCode, isValidPhoneNumber } = useCountryCodes();

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar({ ...snackbar, open: false });
    };

    const capitalizeFirstLetter = (str) => {
        if (!str) return "";
        if (typeof str !== "string") return str;
        return str
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user information for ${userName}.`, severity: "info" });
        }
    };

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setError("");
            try {
                const response = await axiosInstance.get(`/travel-order-pax/${id}`);
                if (!cancelled) setPax(response.data);
            } catch (err) {
                if (!cancelled) {
                    setPax(null);
                    setError("Traveler not found");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [axiosInstance, id]);

    useEffect(() => {
        if (!pax?.is_pax_cancelled) {
            setCancellation(null);
            return undefined;
        }
        let cancelled = false;
        const loadCancellation = async () => {
            setCancellationLoading(true);
            setCancellationError("");
            try {
                const response = await axiosInstance.get(`/travel-order-pax/${id}/cancellation`);
                if (!cancelled) setCancellation(response.data || null);
            } catch (err) {
                if (!cancelled) {
                    setCancellation(null);
                    setCancellationError("Unable to load cancellation details.");
                }
            } finally {
                if (!cancelled) setCancellationLoading(false);
            }
        };
        loadCancellation();
        return () => { cancelled = true; };
    }, [axiosInstance, id, pax?.is_pax_cancelled]);

    // --- Edit mode handlers ---

    const handleEditClick = () => {
        if (isOrderCancelledOrFailed) return;
        setEditedPax({ ...pax });
        setFieldErrors({});
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        setEditedPax(null);
        setFieldErrors({});
        setIsEditMode(false);
    };

    const handleFieldChange = (field) => (value) => {
        setEditedPax((prev) => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSave = async () => {
        const { isValid, errors, firstMessage, firstErrorField } = validateEditedPax(editedPax, {
            isValidPhoneNumber,
            departureDate: pax?.departure_date,
        });
        setFieldErrors(errors);

        if (!isValid) {
            setSnackbar({ open: true, message: firstMessage, severity: "error" });
            if (firstErrorField) {
                const el = document.getElementById(`edit-field-${firstErrorField}`);
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...editedPax,
                passenger_name: composeDisplayName(editedPax),
                date_of_birth: toApiDateString(editedPax.date_of_birth),
                passport_issue_date: toApiDateString(editedPax.passport_issue_date),
                passport_expiry_date: toApiDateString(editedPax.passport_expiry_date),
            };
            const response = await axiosInstance.put(`/travel-order-pax/${id}`, payload);
            setPax(response.data ?? payload);
            setIsEditMode(false);
            setEditedPax(null);
            setFieldErrors({});
            setSnackbar({ open: true, message: "Traveler details updated successfully.", severity: "success" });
        } catch (err) {
            const passportErrors = err?.response?.data?.detail?.passport_errors;
            const hasPassportErrors = Array.isArray(passportErrors) && passportErrors.length > 0;
            const message = hasPassportErrors
                ? passportErrors.join("; ")
                : getApiErrorMessage(err, "Failed to update traveler details. Please try again.");

            if (hasPassportErrors) {
                setFieldErrors((prev) => ({ ...prev, passport_expiry_date: passportErrors[0] }));
                const el = document.getElementById("edit-field-passport_expiry_date");
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }

            setSnackbar({
                open: true,
                message,
                severity: "error"
            });
        }
        finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} display="flex" justifyContent="center">
                    <PearlLogoLoader size="md" message="Loading traveler..." />
                </MDBox>
            </DashboardLayout>
        );
    }

    if (!pax) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} px={3}>
                    <MDTypography variant="h6" mb={2}>
                        {error || "Traveler not found"}
                    </MDTypography>
                    <MDButton variant="gradient" color="info" onClick={() => navigate("/travel-orders")}>
                        Back to Orders
                    </MDButton>
                </MDBox>
            </DashboardLayout>
        );
    }

    // Use the in-progress copy while editing, the saved record otherwise.
    const displayPax = isEditMode ? editedPax : pax;

    const age = getAge(displayPax.date_of_birth);
    const isAdult = age == null || age >= 18;
    const passportExpiryStatus = getPassportExpiryStatus(displayPax.passport_expiry_date);
    const displayName = isEditMode ? composeDisplayName(displayPax) : displayPax.passenger_name;

    // These sections contain NO editable fields (no wizard equivalent), so
    // their visibility never depends on isEditMode.
    const hasEmergencyContact =
        pax.emergency_contact_name || pax.emergency_contact_phone || pax.emergency_email || pax.emergency_relationship;
    const showEmergencySection = isEditMode || hasEmergencyContact;

    // Passport section has a mix of editable + non-editable fields, so it
    // should open up in edit mode even if empty, to allow filling required ones.
    const showPassportSection =
        isEditMode ||
        displayPax.passport_number ||
        displayPax.passport_issue_date ||
        displayPax.passport_expiry_date ||
        displayPax.passport_issue_place;

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>

                    {/* HEADER */}

                    <Grid container spacing={3} mb={3}>
                        <Grid item xs={12}>
                            <Card sx={{ borderRadius: "12px", border: "1px solid #E8EDF5", boxShadow: "none" }}>
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap={2}
                                    p={2.5}
                                >
                                    <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                        <MDBox
                                            sx={{
                                                width: 52, height: 52, borderRadius: "50%",
                                                bgcolor: "#E3F2FD", color: "#1565C8",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontWeight: 700, fontSize: "1rem", flexShrink: 0,
                                            }}
                                        >
                                            {getInitials(displayName)}
                                        </MDBox>

                                        <MDBox>
                                            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                {/* Name is not directly editable here — it's a read-only
                                                composite of first/middle/last, which are edited below
                                                in Personal Information. */}
                                                <MDTypography variant="h6" fontWeight="bold" sx={{ fontSize: "1.1rem" }}>
                                                    {displayName}
                                                </MDTypography>
                                                {pax.is_primary && (
                                                    <Chip
                                                        label="Primary Traveler"
                                                        size="small"
                                                        sx={{ bgcolor: "#E3F2FD", color: "#1565C8", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                                    />
                                                )}
                                                <Chip
                                                    label={pax.status}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: pax.status === "Active" ? "#E8F5E9" : "#F0F0F0",
                                                        color: pax.status === "Active" ? "#2E7D32" : "#616161",
                                                        fontWeight: 600, fontSize: "0.65rem", height: 20,
                                                    }}
                                                />
                                                {pax.is_pax_cancelled && (
                                                    <Chip
                                                        label="Cancelled"
                                                        size="small"
                                                        sx={{ bgcolor: "#FDECEA", color: "#C62828", fontWeight: 600, fontSize: "0.65rem", height: 20 }}
                                                    />
                                                )}
                                            </MDBox>
                                            <MDTypography variant="button" color="text" fontWeight="regular" display="block" mt={0.5}>
                                                {isAdult ? "Adult" : "Child"}{age != null ? `, ${age} yrs` : ""} · {displayPax.gender || "-"}
                                                {displayPax.departure_city ? ` · Departing from ${displayPax.departure_city}` : ""}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    {/* Edit / Save / Cancel controls */}

                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        {!isEditMode ? (
                                            pax.is_pax_cancelled || isOrderCancelledOrFailed ? null : (
                                                <MDButton variant="gradient" color="warning" startIcon={<EditIcon />} onClick={handleEditClick}>
                                                    Edit
                                                </MDButton>
                                            )
                                        ) : (
                                            <>
                                                <MDButton
                                                    variant="text"
                                                    color="secondary"
                                                    size="small"
                                                    onClick={handleCancelEdit}
                                                    disabled={isSaving}
                                                >
                                                    Cancel
                                                </MDButton>
                                                <MDButton
                                                    variant="gradient"
                                                    color="info"
                                                    size="small"
                                                    onClick={handleSave}
                                                    disabled={isSaving}
                                                    startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : <Icon>save</Icon>}
                                                >
                                                    {isSaving ? "Saving..." : "Save"}
                                                </MDButton>
                                            </>
                                        )}
                                    </MDBox>
                                </MDBox>
                            </Card>
                        </Grid>
                    </Grid>

                    {isOrderCancelledOrFailed && (
                        <Grid container spacing={3} mb={3}>
                            <Grid item xs={12}>
                                <Alert severity="error">
                                    This traveler belongs to a booking that is {pax.order_status || "Cancelled"} and cannot be edited. All details are read-only.
                                </Alert>
                            </Grid>
                        </Grid>
                    )}

                    <Grid container spacing={3}>

                        {/* Main column */}

                        <Grid item xs={12} lg={8}>
                            <Grid container spacing={3}>

                                {pax.is_pax_cancelled && (
                                    <Grid item xs={12}>
                                        <InfoCard
                                            icon="event_busy"
                                            title="Cancellation & Refund"
                                            headerAction={cancellation && <RefundStatusChip status={cancellation.refund_status} />}
                                        >
                                            <PaxCancellationDetails
                                                cancellation={cancellation}
                                                loading={cancellationLoading}
                                                error={cancellationError}
                                            />
                                        </InfoCard>
                                    </Grid>
                                )}

                                {/* Personal Information */}

                                <Grid item xs={12} md={6}>
                                    <InfoCard icon="person" title="Personal Information">
                                        <Grid container rowSpacing={2.5} columnSpacing={2}>

                                            {!isEditMode ? (
                                                <DetailField label="Full Name" value={pax.passenger_name} xs={12} md={12} />
                                            ) : (
                                                <>

                                                    <EditableField
                                                        label="First Name (as per passport)" fieldKey="first_name" xs={4} md={4} required
                                                        placeholder="First Name"
                                                        value={displayPax.first_name} isEditing
                                                        error={fieldErrors.first_name}
                                                        onChange={handleFieldChange("first_name")}
                                                    />
                                                    <EditableField
                                                        label="Middle Name (as per passport)" fieldKey="middle_name" xs={4} md={4}
                                                        placeholder="Middle Name"
                                                        value={displayPax.middle_name} isEditing
                                                        onChange={handleFieldChange("middle_name")}
                                                    />
                                                    <EditableField
                                                        label="Last Name (as per passport)" fieldKey="last_name" xs={4} md={4}
                                                        placeholder="Last Name"
                                                        value={displayPax.last_name} isEditing
                                                        onChange={handleFieldChange("last_name")}
                                                    />
                                                </>
                                            )}

                                            <EditableField
                                                label="Date of Birth" fieldKey="date_of_birth" xs={6} md={6} required
                                                type="date"
                                                value={displayPax.date_of_birth}
                                                displayValue={formatDate(displayPax.date_of_birth)}
                                                isEditing={isEditMode}
                                                error={fieldErrors.date_of_birth}
                                                onChange={handleFieldChange("date_of_birth")}
                                            />
                                            <DetailField label="Age" value={age != null ? `${age} years` : null} xs={6} md={6} />
                                            <EditableField
                                                label="Gender" fieldKey="gender" xs={6} md={6} required
                                                type="select"
                                                options={GENDER_OPTIONS}
                                                value={displayPax.gender}
                                                isEditing={isEditMode}
                                                error={fieldErrors.gender}
                                                onChange={handleFieldChange("gender")}
                                            />
                                            <EditableField
                                                label="Nationality" fieldKey="nationality" xs={6} md={6} required
                                                type="autocomplete"
                                                options={countries}
                                                value={displayPax.nationality}
                                                isEditing={isEditMode}
                                                error={fieldErrors.nationality}
                                                onChange={handleFieldChange("nationality")}
                                            />
                                        </Grid>
                                    </InfoCard>
                                </Grid>

                                {/* Contact Information */}

                                <Grid item xs={12} md={6}>
                                    <InfoCard icon="contact_mail" title="Contact Information">
                                        <Grid container rowSpacing={2.5} columnSpacing={2}>
                                            <EditableField
                                                label="Email" fieldKey="email" xs={12} md={12} required
                                                value={displayPax.email}
                                                isEditing={isEditMode}
                                                error={fieldErrors.email}
                                                onChange={handleFieldChange("email")}
                                            />

                                            {!isEditMode ? (
                                                <DetailField
                                                    label="Phone"
                                                    value={displayPax.mobile ? `${displayPax.dial_code || ""} ${displayPax.mobile}`.trim() : null}
                                                    xs={12} md={12}
                                                />
                                            ) : (
                                                <Grid item xs={12} md={12} id="edit-field-mobile">
                                                    <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
                                                        Phone
                                                        <MDBox component="span" sx={{ color: "#E53935", ml: 0.25 }}>*</MDBox>
                                                    </MDTypography>
                                                    <MDBox display="flex" gap={1}>
                                                        <Autocomplete
                                                            size="small"
                                                            disableClearable
                                                            options={countryCodes}
                                                            filterOptions={filterCountryCodeOptions}
                                                            getOptionLabel={(option) => option.dialCode}
                                                            value={getByDialCode(displayPax.dial_code) || null}
                                                            onChange={(_, value) => handleFieldChange("dial_code")(value?.dialCode || "")}
                                                            isOptionEqualToValue={(option, value) => option.dialCode === value.dialCode}
                                                            renderOption={(props, option) => (
                                                                <MDBox component="li" {...props} key={option.iso2} display="flex" gap={1} sx={{ whiteSpace: "nowrap" }}>
                                                                    <MDTypography variant="caption" color="text">{option.dialCode}</MDTypography>
                                                                    <MDTypography variant="caption" color="text">{option.country}</MDTypography>
                                                                </MDBox>
                                                            )}
                                                            slotProps={{ popper: { sx: { width: "260px !important" } } }}
                                                            sx={{ flex: "0 0 35%", minWidth: 0, "& .MuiAutocomplete-input": { minWidth: "30px !important" } }}
                                                            renderInput={(params) => (
                                                                <TextField {...params} placeholder="+91" error={Boolean(fieldErrors.mobile)} />
                                                            )}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            placeholder="98765 43210"
                                                            value={displayPax.mobile || ""}
                                                            error={Boolean(fieldErrors.mobile)}
                                                            onChange={(e) => handleFieldChange("mobile")(e.target.value)}
                                                            sx={{ flex: "1 1 65%", minWidth: 0 }}
                                                        />
                                                    </MDBox>
                                                    {fieldErrors.mobile && (
                                                        <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                                                            {fieldErrors.mobile}
                                                        </MDTypography>
                                                    )}
                                                </Grid>
                                            )}

                                            <EditableField
                                                label="Address" fieldKey="address" xs={12} md={12} required
                                                type="address"
                                                value={displayPax.address}
                                                isEditing={isEditMode}
                                                error={fieldErrors.address}
                                                onChange={handleFieldChange("address")}
                                            />
                                            <EditableField
                                                label="Country" fieldKey="country" xs={12} md={12} required
                                                type="autocomplete"
                                                options={countries}
                                                value={displayPax.country}
                                                isEditing={isEditMode}
                                                error={fieldErrors.country}
                                                onChange={handleFieldChange("country")}
                                            />
                                        </Grid>
                                    </InfoCard>
                                </Grid>

                                {/* Passport & Travel Documents */}

                                <Grid item xs={12}>
                                    <InfoCard
                                        icon="badge"
                                        title="Passport & Travel Documents"
                                        headerAction={
                                            passportExpiryStatus && !isEditMode && (
                                                <Chip
                                                    size="small"
                                                    label={passportExpiryStatus.label}
                                                    sx={{ bgcolor: passportExpiryStatus.bg, color: passportExpiryStatus.color, fontWeight: 700, fontSize: "0.7rem" }}
                                                />
                                            )
                                        }
                                    >
                                        {showPassportSection ? (
                                            <Grid container rowSpacing={2.5} columnSpacing={2}>
                                                <EditableField
                                                    label="Passport Number" fieldKey="passport_number" xs={6} md={3}
                                                    value={displayPax.passport_number}
                                                    displayValue={<MonoText>{displayPax.passport_number}</MonoText>}
                                                    isEditing={isEditMode}
                                                    error={fieldErrors.passport_number}
                                                    onChange={handleFieldChange("passport_number")}
                                                />
                                                <EditableField
                                                    label="Issue Place" fieldKey="passport_issue_place" xs={6} md={3}
                                                    value={displayPax.passport_issue_place}
                                                    isEditing={isEditMode}
                                                    onChange={handleFieldChange("passport_issue_place")}
                                                />
                                                <EditableField
                                                    label="Issue Date" fieldKey="passport_issue_date" xs={6} md={3}
                                                    type="date"
                                                    value={displayPax.passport_issue_date}
                                                    displayValue={formatDate(displayPax.passport_issue_date)}
                                                    isEditing={isEditMode}
                                                    error={fieldErrors.passport_issue_date}
                                                    onChange={handleFieldChange("passport_issue_date")}
                                                />
                                                <EditableField
                                                    label="Expiry Date" fieldKey="passport_expiry_date" xs={6} md={3}
                                                    type="date"
                                                    value={displayPax.passport_expiry_date}
                                                    displayValue={formatDate(displayPax.passport_expiry_date)}
                                                    isEditing={isEditMode}
                                                    error={fieldErrors.passport_expiry_date}
                                                    onChange={handleFieldChange("passport_expiry_date")}
                                                />

                                                {/* Not part of the wizard — always read-only */}
                                                <DetailField label="Passport Country" value={pax.passport_country} xs={6} md={3} />
                                                <DetailField label="Travel Insurance No." value={pax.travel_insurance_number} xs={6} md={3} />
                                            </Grid>
                                        ) : (
                                            <EmptyFieldsNotice message="No passport details on file for this traveler." />
                                        )}
                                    </InfoCard>
                                </Grid>

                                {/* Emergency Contact — not collected by the wizard, always read-only */}

                                <Grid item xs={12} md={6}>
                                    <InfoCard icon="emergency" title="Emergency Contact">
                                        {showEmergencySection ? (
                                            <Grid container rowSpacing={2.5} columnSpacing={2}>
                                                <EditableField
                                                    label="Name" fieldKey="emergency_contact_name" xs={6} md={6}
                                                    value={displayPax.emergency_contact_name}
                                                    isEditing={isEditMode}
                                                    onChange={handleFieldChange("emergency_contact_name")}
                                                />
                                                <EditableField
                                                    label="Relationship" fieldKey="emergency_relationship" xs={6} md={6}
                                                    value={displayPax.emergency_relationship}
                                                    isEditing={isEditMode}
                                                    onChange={handleFieldChange("emergency_relationship")}
                                                />
                                                <EditableField
                                                    label="Phone" fieldKey="emergency_contact_phone" xs={6} md={6}
                                                    value={displayPax.emergency_contact_phone}
                                                    isEditing={isEditMode}
                                                    onChange={handleFieldChange("emergency_contact_phone")}
                                                />
                                                <EditableField
                                                    label="Email" fieldKey="emergency_email" xs={6} md={6}
                                                    value={displayPax.emergency_email}
                                                    isEditing={isEditMode}
                                                    error={fieldErrors.emergency_email}
                                                    onChange={handleFieldChange("emergency_email")}
                                                />
                                            </Grid>
                                        ) : (
                                            <EmptyFieldsNotice message="No emergency contact provided." />
                                        )}
                                    </InfoCard>
                                </Grid>

                                {/* Special Details — dietary/allergies/medical are not collected
                                individually by the wizard (it has one combined free-text field),
                                so those three stay read-only. special_request maps to that field
                                and is editable. */}

                                <Grid item xs={12} md={6}>
                                    <InfoCard icon="assignment" title="Special Details">
                                        <Grid container rowSpacing={2.5} columnSpacing={2}>
                                            {pax.dietary_preferences && (
                                                <DetailField label="Dietary Preferences" value={pax.dietary_preferences} xs={12} md={12} />
                                            )}
                                            {pax.allergies && (
                                                <DetailField label="Allergies" value={pax.allergies} xs={12} md={12} />
                                            )}
                                            {pax.medical_conditions && (
                                                <DetailField label="Medical Conditions" value={pax.medical_conditions} xs={12} md={12} />
                                            )}
                                            <EditableField
                                                label="Special Details" fieldKey="special_request" xs={12} md={12}
                                                multiline maxLength={SPECIAL_DETAILS_MAX_LENGTH}
                                                placeholder="E.g. vegetarian meal, nut allergy, wheelchair assistance, early check-in..."
                                                value={displayPax.special_request}
                                                isEditing={isEditMode}
                                                onChange={handleFieldChange("special_request")}
                                            />
                                            {!pax.dietary_preferences && !pax.allergies && !pax.medical_conditions && !pax.special_request && !isEditMode && (
                                                <Grid item xs={12}>
                                                    <EmptyFieldsNotice message="No dietary, medical, or special details noted." />
                                                </Grid>
                                            )}
                                        </Grid>
                                    </InfoCard>
                                </Grid>

                            </Grid>
                        </Grid>

                        {/* Right rail — unchanged, not editable inline */}

                        <Grid item xs={12} lg={4}>
                            <MDBox display="flex" flexDirection="column" gap={3}>

                                <InfoCard icon="confirmation_number" title="Associated Order">
                                    <MDBox display="flex" flexDirection="column" gap={1.5}>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                            <MDTypography variant="caption" color="text">Booking Reference</MDTypography>
                                            <MonoText variant="button" fontWeight="bold">
                                                #{pax.travel_order_id}
                                            </MonoText>
                                        </MDBox>
                                        <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            fullWidth
                                            disabled={isEditMode}
                                            onClick={() => navigate(`/travel-order/${pax.travel_order_id}`)}
                                        >
                                            View Order Details
                                        </MDButton>
                                    </MDBox>
                                </InfoCard>

                                <InfoCard icon="info" title="Audit Details">
                                    <MDBox display="flex" flexDirection="column" gap={1.25}>
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDTypography variant="caption" color="text">Created</MDTypography>
                                            <MDTypography variant="caption" fontWeight="medium" color="dark">
                                                {formatDateTime(pax.created_at)}
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDTypography variant="caption" color="text">Created By</MDTypography>
                                            <MDTypography
                                                component="span"
                                                onClick={() => handleUserClick(pax.created_by, pax.created_by_name)}
                                                variant="button"
                                                color="info"
                                                fontWeight="medium"
                                                sx={{ cursor: "pointer", ml: 1 }}
                                            >
                                                {capitalizeFirstLetter(pax.created_by_name || "")}
                                            </MDTypography>
                                        </MDBox>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDTypography variant="caption" color="text">Last Updated</MDTypography>
                                            <MDTypography variant="caption" fontWeight="medium" color="dark">
                                                {formatDateTime(pax.updated_at)}
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox display="flex" justifyContent="space-between">
                                            <MDTypography variant="caption" color="text">Updated By</MDTypography>
                                            <MDTypography
                                                component="span"
                                                onClick={() => handleUserClick(pax.updated_by, pax.updated_by_name)}
                                                variant="button"
                                                color="info"
                                                fontWeight="medium"
                                                sx={{ cursor: "pointer", ml: 1 }}
                                            >
                                                {capitalizeFirstLetter(pax.updated_by_name || "")}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                </InfoCard>

                            </MDBox>
                        </Grid>
                    </Grid>
                </MDBox>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </DashboardLayout>
        </LocalizationProvider>
    );
}

export default PaxDetailView;